import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import { dateTimeToSGTIso } from '../utils/dateUtils';
import '../assets/AuditSummary.css';

const AuditSummary = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Date range state
  const [dateRange, setDateRange] = useState('thisMonth');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    fetchSummary();
  }, []);

  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case 'thisMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last3Months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'thisYear':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          // Anchor the picked YYYY-MM-DD boundaries to SGT so the filter day
          // doesn't shift when the browser is west of UTC. Return ISO strings
          // directly (same format the preset cases send below).
          return {
            startDate: dateTimeToSGTIso(customStartDate, '00:00'),
            endDate: dateTimeToSGTIso(customEndDate, '23:59')
          };
        }
        break;
      default:
        return {};
    }

    if (startDate && endDate) {
      return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };
    }
    return {};
  };

  const fetchSummary = async () => {
    setLoading(true);
    setError('');

    try {
      const params = getDateRange();
      const response = await adminApi.getInvoiceAuditSummary(params);
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to fetch audit summary:', err);
      setError(
        err.response?.data?.message || 
        'Failed to load audit summary. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    if (newRange !== 'custom') {
      // Auto-fetch when selecting predefined ranges
      setTimeout(() => {
        fetchSummary();
      }, 100);
    }
  };

  const handleCustomDateApply = () => {
    if (customStartDate && customEndDate) {
      fetchSummary();
    }
  };

  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2)}`;
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  const getEmailSuccessRate = () => {
    if (!summary || !summary.summary.totalEmailed) return 0;
    return ((summary.summary.emailsSent / summary.summary.totalEmailed) * 100).toFixed(1);
  };

  return (
    <div className="audit-summary">
      <div className="audit-summary-header">
        <h2>Invoice Audit Summary</h2>
        <p className="audit-summary-description">
          Overview of invoice generation and delivery statistics
        </p>
      </div>

      {/* Date Range Selector */}
      <div className="date-range-selector">
        <div className="date-range-buttons">
          <button
            className={dateRange === 'thisMonth' ? 'active' : ''}
            onClick={() => handleDateRangeChange('thisMonth')}
          >
            This Month
          </button>
          <button
            className={dateRange === 'lastMonth' ? 'active' : ''}
            onClick={() => handleDateRangeChange('lastMonth')}
          >
            Last Month
          </button>
          <button
            className={dateRange === 'last3Months' ? 'active' : ''}
            onClick={() => handleDateRangeChange('last3Months')}
          >
            Last 3 Months
          </button>
          <button
            className={dateRange === 'thisYear' ? 'active' : ''}
            onClick={() => handleDateRangeChange('thisYear')}
          >
            This Year
          </button>
          <button
            className={dateRange === 'custom' ? 'active' : ''}
            onClick={() => handleDateRangeChange('custom')}
          >
            Custom Range
          </button>
        </div>

        {dateRange === 'custom' && (
          <div className="custom-date-range">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              placeholder="Start Date"
            />
            <span>to</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              placeholder="End Date"
            />
            <button onClick={handleCustomDateApply}>Apply</button>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading summary...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="error-state">
          <p className="error-message">{error}</p>
          <button className="retry-btn" onClick={fetchSummary}>
            Retry
          </button>
        </div>
      )}

      {/* Summary Content */}
      {!loading && !error && summary && (
        <>
          {/* Key Metrics Cards */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                </svg>
              </div>
              <div className="metric-content">
                <div className="metric-label">Total Invoices</div>
                <div className="metric-value">{formatNumber(summary.summary.totalInvoices)}</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
                </svg>
              </div>
              <div className="metric-content">
                <div className="metric-label">Total Amount</div>
                <div className="metric-value">{formatCurrency(summary.summary.totalAmount)}</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
                </svg>
              </div>
              <div className="metric-content">
                <div className="metric-label">PDFs Exported</div>
                <div className="metric-value">{formatNumber(summary.summary.totalExported)}</div>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </div>
              <div className="metric-content">
                <div className="metric-label">Emails Sent</div>
                <div className="metric-value">
                  {formatNumber(summary.summary.emailsSent)} / {formatNumber(summary.summary.totalEmailed)}
                </div>
                <div className="metric-subtitle">
                  {getEmailSuccessRate()}% success rate
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown Sections */}
          <div className="breakdown-sections">
            {/* By Invoice Type */}
            <div className="breakdown-card">
              <h3>By Invoice Type</h3>
              <div className="breakdown-content">
                {summary.breakdown.byType && summary.breakdown.byType.length > 0 ? (
                  <table className="breakdown-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Count</th>
                        <th>Total Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.breakdown.byType.map((item) => (
                        <tr key={item._id}>
                          <td className="type-cell">
                            <span className={`type-badge ${item._id}`}>
                              {item._id}
                            </span>
                          </td>
                          <td>{formatNumber(item.count)}</td>
                          <td className="amount-cell">{formatCurrency(item.totalAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data">No data available</p>
                )}
              </div>
            </div>

            {/* By Action */}
            <div className="breakdown-card">
              <h3>By Action</h3>
              <div className="breakdown-content">
                {summary.breakdown.byAction && summary.breakdown.byAction.length > 0 ? (
                  <table className="breakdown-table">
                    <thead>
                      <tr>
                        <th>Action</th>
                        <th>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.breakdown.byAction.map((item) => (
                        <tr key={item._id}>
                          <td>
                            <span className={`action-badge ${item._id}`}>
                              {item._id}
                            </span>
                          </td>
                          <td>{formatNumber(item.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data">No data available</p>
                )}
              </div>
            </div>

            {/* Top Administrators */}
            <div className="breakdown-card full-width">
              <h3>Top Administrators</h3>
              <div className="breakdown-content">
                {summary.topAdministrators && summary.topAdministrators.length > 0 ? (
                  <table className="breakdown-table">
                    <thead>
                      <tr>
                        <th>Administrator</th>
                        <th>Email</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.topAdministrators.map((item) => (
                        <tr key={item._id}>
                          <td className="admin-name">
                            {item.administrator?.name || 'Unknown'}
                          </td>
                          <td className="admin-email">
                            {item.administrator?.email || 'N/A'}
                          </td>
                          <td>{formatNumber(item.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data">No data available</p>
                )}
              </div>
            </div>
          </div>

          {/* Email Delivery Status */}
          {summary.summary.emailsFailed > 0 && (
            <div className="alert-card">
              <div className="alert-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                </svg>
              </div>
              <div className="alert-content">
                <h4>Email Delivery Issues</h4>
                <p>
                  {formatNumber(summary.summary.emailsFailed)} email(s) failed to deliver.
                  Please check the audit trail for details.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditSummary;
